import type {
    DivisionBar,
    DoorConfig,
    DoorTypeId,
    DivisionId,
    GlassId,
    FinishId,
    ProfileId,
    HandleId,
    HandleFinishId,
    SidePanelOption,
    HingeSide
} from '@/lib/configurator/options'

import {
    DEFAULT_CONFIG,
    DOOR_TYPES,
    DIVISIONS,
    GLASS_TYPES,
    FINISHES,
    PROFILES,
    HANDLES,
    HANDLE_FINISHES,
    NOTE_MAX_LENGTH,
    SIDE_PANEL_OPTIONS,
    SIDE_PANEL_RANGE,
    TRANSOM_RANGE,
    findDoorType,
    findDivision,
    normalizeRalCode
} from '@/lib/configurator/options'

import { presetBarsMm, rescaleBars } from '@/lib/configurator/layout'

const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T => {
    return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback
}

const clampNumber = (value: unknown, min: number, max: number, fallback: number): number => {
    const number = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN

    if (!Number.isFinite(number)) return fallback

    return Math.min(max, Math.max(min, Math.round(number)))
}

const bool = (value: unknown, fallback: boolean): boolean => {
    if (typeof value === 'boolean') return value
    if (value === 1 || value === '1' || value === 'true') return true
    if (value === 0 || value === '0' || value === 'false') return false
    return fallback
}

const note = (value: unknown): string => {
    if (typeof value !== 'string') return ''

    return value.replace(/[\u0000-\u001F\u007F<>]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, NOTE_MAX_LENGTH)
}

const LEGACY_GLASS: Record<string, { glass: GlassId, note?: string }> = {
    'extra-helder': { glass: 'helder' },
    'satijn': { glass: 'mat' },
    'rookglas': { glass: 'op-aanvraag', note: 'Rookglas grijs' },
    'brons': { glass: 'op-aanvraag', note: 'Bronsglas' },
    'ribbel': { glass: 'op-aanvraag', note: 'Ribbelglas' }
}

const LEGACY_FINISH: Record<string, { finish: FinishId, ral?: string }> = {
    'structuur-zwart': { finish: 'ral9005' },
    'gebroken-wit': { finish: 'ral9016' },
    'antraciet': { finish: 'ral', ral: 'RAL 7016' },
    'brons': { finish: 'ral', ral: 'Brons metallic' },
    'champagne': { finish: 'ral', ral: 'Champagne metallic' }
}

const LEGACY_HANDLE: Record<string, HandleId> = {
    staaf: 'lang',
    beugel: 'klein',
    hendel: 'kruk',
    knop: 'kruk',
    kom: 'klein'
}

const normalizeBars = (value: unknown): DivisionBar[] => {
    if (!Array.isArray(value)) return []

    const position = (input: unknown): number | null => {
        if (input === null || input === undefined || input === '') return null

        const number = typeof input === 'number' ? input : Number(input)

        return Number.isFinite(number) ? Math.min(6000, Math.max(0, Math.round(number))) : null
    }

    return value
        .slice(0, 24)
        .map(item => (item && typeof item === 'object' ? item as Record<string, unknown> : null))
        .filter((item): item is Record<string, unknown> => Boolean(item))
        .map(item => ({
            orientation: item.orientation === 'v' ? 'v' as const : 'h' as const,
            at: position(item.at) ?? 0,
            from: position(item.from),
            to: position(item.to)
        }))
        .filter(bar => bar.at > 0)
}

export const normalizeConfig = (input: unknown): DoorConfig => {
    const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>

    const doorType = pick<DoorTypeId>(raw.doorType, DOOR_TYPES.map(item => item.id), DEFAULT_CONFIG.doorType)
    const type = findDoorType(doorType)

    const division = pick<DivisionId>(raw.division, DIVISIONS.map(item => item.id), DEFAULT_CONFIG.division)
    const divisionOption = findDivision(division)

    const rows = divisionOption.adjustRows
        ? clampNumber(raw.rows, divisionOption.rows.min, divisionOption.rows.max, divisionOption.rows.default)
        : 0

    const cols = divisionOption.adjustCols
        ? clampNumber(raw.cols, divisionOption.cols.min, divisionOption.cols.max, divisionOption.cols.default)
        : 0

    const legacyGlass = typeof raw.glass === 'string' ? LEGACY_GLASS[raw.glass] : undefined
    const glass = legacyGlass ? legacyGlass.glass : pick<GlassId>(raw.glass, GLASS_TYPES.map(item => item.id), DEFAULT_CONFIG.glass)
    const glassNote = glass === 'op-aanvraag' ? note(raw.glassNote) || legacyGlass?.note || '' : ''

    const legacyFinish = typeof raw.finish === 'string' ? LEGACY_FINISH[raw.finish] : undefined
    const finish = legacyFinish ? legacyFinish.finish : pick<FinishId>(raw.finish, FINISHES.map(item => item.id), DEFAULT_CONFIG.finish)
    const ralCode = finish === 'ral' ? normalizeRalCode(note(raw.ralCode) || legacyFinish?.ral || '') : ''

    const rawHandle = typeof raw.handle === 'string' && LEGACY_HANDLE[raw.handle] ? LEGACY_HANDLE[raw.handle] : raw.handle

    let handle = pick<HandleId>(rawHandle, HANDLES.map(item => item.id), DEFAULT_CONFIG.handle)

    if (!type.handles.includes(handle)) handle = type.handles.includes('lang') ? 'lang' : type.handles[0]

    const sidePanels = type.allowsSidePanels
        ? pick<SidePanelOption>(raw.sidePanels, SIDE_PANEL_OPTIONS.map(item => item.id), 'geen')
        : 'geen'

    return {
        version: 2,
        doorType,
        width: clampNumber(raw.width, type.width.min, type.width.max, type.width.default),
        height: clampNumber(raw.height, type.height.min, type.height.max, type.height.default),
        division,
        rows,
        cols,
        bars: division === 'eigen' ? normalizeBars(raw.bars) : [],
        glass,
        glassNote,
        finish,
        ralCode,
        profile: pick<ProfileId>(raw.profile, PROFILES.map(item => item.id), DEFAULT_CONFIG.profile),
        handle,
        handleNote: handle === 'op-aanvraag' ? note(raw.handleNote) : '',
        handleFinish: pick<HandleFinishId>(raw.handleFinish, HANDLE_FINISHES.map(item => item.id), DEFAULT_CONFIG.handleFinish),
        sidePanels,
        sidePanelWidth: clampNumber(raw.sidePanelWidth, SIDE_PANEL_RANGE.min, SIDE_PANEL_RANGE.max, SIDE_PANEL_RANGE.default),
        transom: type.allowsTransom ? bool(raw.transom, false) : false,
        transomHeight: clampNumber(raw.transomHeight, TRANSOM_RANGE.min, TRANSOM_RANGE.max, TRANSOM_RANGE.default),
        bottomPanel: bool(raw.bottomPanel, false),
        hinge: pick<HingeSide>(raw.hinge, ['links', 'rechts'], 'links'),
        lock: bool(raw.lock, false),
        softClose: doorType === 'schuifdeur' ? bool(raw.softClose, false) : false
    }
}

export const parseStoredConfig = (value: unknown): DoorConfig => {
    if (typeof value === 'string') {
        try {
            return normalizeConfig(JSON.parse(value))
        }
        catch {
            return normalizeConfig({})
        }
    }

    return normalizeConfig(value)
}

const GEOMETRY_KEYS: (keyof DoorConfig)[] = ['doorType', 'width', 'height', 'bottomPanel', 'profile']

export const applyChange = (previous: DoorConfig, patch: Partial<DoorConfig>): DoorConfig => {
    const next = normalizeConfig({ ...previous, ...patch })

    if (next.division !== 'eigen' || 'bars' in patch) return next

    const geometryChanged = GEOMETRY_KEYS.some(key => key in patch && patch[key] !== previous[key])

    if (!geometryChanged || previous.division !== 'eigen') return next

    return normalizeConfig({ ...next, bars: rescaleBars(previous, next) })
}

export const applyDoorTypeDefaults = (config: DoorConfig, doorType: DoorTypeId): DoorConfig => {
    const type = findDoorType(doorType)

    return applyChange(config, { doorType, width: type.width.default, height: type.height.default })
}

export const applyDivisionDefaults = (config: DoorConfig, division: DivisionId): DoorConfig => {
    const option = findDivision(division)

    if (division === 'eigen') {
        return normalizeConfig({ ...config, division: 'eigen', bars: presetBarsMm(config) })
    }

    return normalizeConfig({
        ...config,
        division,
        rows: option.rows.default,
        cols: option.cols.default,
        bars: []
    })
}

export const toCustomBars = (config: DoorConfig, bars: DivisionBar[]): DoorConfig => {
    return normalizeConfig({ ...config, division: 'eigen', bars })
}

export const defaultConfig = (): DoorConfig => normalizeConfig(DEFAULT_CONFIG)

export const encodeConfig = (config: DoorConfig): string => {
    const json = JSON.stringify(config)
    const bytes = new TextEncoder().encode(json)
    let binary = ''

    bytes.forEach(byte => {
        binary += String.fromCharCode(byte)
    })

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
